<script setup lang="ts">
import { Trash2 } from 'lucide-vue-next'
import Button from 'pejer/src/components/ui/button/Button.vue'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'pejer/src/components/ui/dialog'
import { toast } from 'pejer/src/components/ui/toast'
import { ref } from 'vue'
import { usePejerBase } from '../../stores/pejerData'
import { Users } from '../../tables/Users'
import usePejerMutation from '../../usePejerMutation'
import DataStateButton from '../DataState/DataStateButton.vue'
import ProtectedButton from '../ProtectedButton.vue'

const props = defineProps<{
  user: Users.Full
}>()

const emits = defineEmits<{
  (e: 'deleted'): void
}>()

const pejer = usePejerBase()
const modalOpen = ref(false)

const { mutateAsync, isPending, dataState } = usePejerMutation(pejer.urls.users.delete, {
  queryKeys: [['users']],
  onSuccess: () => {
    toast({
      title: 'User deleted successfully',
    })
    emits('deleted')
    modalOpen.value = false
  },
})

const deleteUser = async () => {
  await mutateAsync({
    pathArgs: {
      id: props.user.userId,
    },
    body: {
      ...(props.user.platformID && { platformID: props.user.platformID }),
    },
  })
}
</script>
<template>
  <div class="flex justify-between">
    <Dialog v-model:open="modalOpen">
      <DialogTrigger v-if="!isPending">
        <ProtectedButton :url="pejer.urls.users.delete" variant="ghost" class="p-2">
          <Trash2 :size="14" />
        </ProtectedButton>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you sure you want to delete {{ user.email }}?</DialogTitle>
          <DialogDescription> This action cannot be reversed. </DialogDescription>
        </DialogHeader>

        <DialogFooter class="justify-end items-end">
          <DialogClose as-child>
            <Button variant="outline"> Cancel </Button>
          </DialogClose>
          <DataStateButton @click="deleteUser()" variant="destructive" :data-state="dataState">
            Delete
          </DataStateButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
